import React, { useMemo, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

// Configuración de Mapbox
if (typeof window !== "undefined") {
    const workerUrl = "https://api.mapbox.com/mapbox-gl-js/v3.11.0/mapbox-gl-csp-worker.js";
    const blobCode = `importScripts('${workerUrl}');`;
    const workerBlob = new Blob([blobCode], { type: "application/javascript" });
    mapboxgl.workerUrl = URL.createObjectURL(workerBlob);
}
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// =========================================================================
// FUNCIONES AUXILIARES
// =========================================================================
const isValidCoordinate = (lat, lng) => {
    const parsedLat = Number(lat);
    const parsedLng = Number(lng);
    return (
        Number.isFinite(parsedLat) &&
        Number.isFinite(parsedLng) &&
        parsedLat >= -90 &&
        parsedLat <= 90 &&
        parsedLng >= -180 &&
        parsedLng <= 180
    );
};

const escapeHtml = (value) =>
    String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");

// Fórmula de Haversine para distancia en kilómetros (aproximación)
const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radio terrestre en km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// =========================================================================
// COMPONENTE: MAPA DE UBICACIONES DE PASAJEROS (CON DISTANCIAS)
// =========================================================================
const PassengerLocationMap = ({ travelers, origenid, paradas }) => {

    const mapContainer = useRef(null);
    const map = useRef(null);
    const markersRef = useRef([]);
    const linesSourceRef = useRef(null);

    // Mapa de paradas por ID
    const stopsMap = useMemo(() => {
        const map = new Map();
        (paradas || []).forEach((p) => {
            map.set(p.locid, p);
        });
        return map;
    }, [paradas]);

    // Construir puntos: origen y pasajeros (con sus distancias)
    const { origen, passengers } = useMemo(() => {
        let origenPoint = null;
        const passengerPoints = [];

        // --- 1. Intentar obtener origen desde origenid ---
        if (origenid) {
            const origenStop = stopsMap.get(Number(origenid));
            if (origenStop && isValidCoordinate(origenStop.loclat, origenStop.loclng)) {
                origenPoint = {
                    type: "origen",
                    lat: Number(origenStop.loclat),
                    lng: Number(origenStop.loclng),
                    name: origenStop.locnom || "Origen",
                };
            }
        }

        // --- 2. Si origenid es null, deducir origen a partir de puntosubida de los pasajeros ---
        if (!origenPoint) {
            // Buscar el primer pasajero que tenga un punto de subida definido
            const firstTraveler = (travelers || []).find((t) => t.puntosubida);
            if (firstTraveler) {
                const subidaNombre = firstTraveler.puntosubida;
                // Buscar en la lista de paradas una que coincida exactamente con el nombre
                const matchingStop = (paradas || []).find(
                    (p) => p.locnom && p.locnom === subidaNombre
                );
                if (matchingStop && isValidCoordinate(matchingStop.loclat, matchingStop.loclng)) {
                    origenPoint = {
                        type: "origen",
                        lat: Number(matchingStop.loclat),
                        lng: Number(matchingStop.loclng),
                        name: matchingStop.locnom || "Origen (deducido)",
                    };
                } else {
                    console.warn(
                        "No se encontró una parada que coincida con el puntosubida:",
                        subidaNombre
                    );
                }
            } else {
                console.warn("No hay pasajeros con puntosubida definido para deducir el origen.");
            }
        }

        // --- 3. Construir puntos de pasajeros (destinos) ---
        (travelers || []).forEach((t) => {
            if (t.destinolat && t.destinolng && isValidCoordinate(t.destinolat, t.destinolng)) {
                const lat = Number(t.destinolat);
                const lng = Number(t.destinolng);
                let distance = null;
                if (origenPoint) {
                    distance = haversineDistance(origenPoint.lat, origenPoint.lng, lat, lng);
                }
                passengerPoints.push({
                    type: "pasajero",
                    lat,
                    lng,
                    name: `${t.pernom || ""} ${t.perape || ""}`.trim() || "Sin nombre",
                    address: t.destinonombre || "Dirección no especificada",
                    distance,
                });
            }
        });

        return { origen: origenPoint, passengers: passengerPoints };
    }, [travelers, stopsMap, paradas, origenid]);

    // Efecto para inicializar el mapa
    useEffect(() => {
        if (!mapContainer.current || !origen || passengers.length === 0) return;
        if (!mapboxgl.accessToken) {
            console.error("Mapbox token no configurado.");
            return;
        }

        // Centrar en el origen
        const center = [origen.lng, origen.lat];

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: "mapbox://styles/mapbox/streets-v11",
            center,
            zoom: 12,
        });

        map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

        map.current.on("load", () => {
            if (!map.current) return;

            const bounds = new mapboxgl.LngLatBounds();
            bounds.extend([origen.lng, origen.lat]);

            // Limpiar marcadores anteriores
            markersRef.current.forEach((m) => m.remove());
            markersRef.current = [];

            // ----- 1. MARCADOR DE ORIGEN -----
            const origenMarker = new mapboxgl.Marker({ color: "#1976d2" })
                .setLngLat([origen.lng, origen.lat])
                .setPopup(
                    new mapboxgl.Popup().setHTML(`
                        <div style="font-family: Arial, sans-serif; min-width: 150px; padding: 4px 0;">
                            <strong style="font-size: 14px;">🏁 Origen</strong><br/>
                            <span style="font-size: 13px; color: #333;">${escapeHtml(origen.name)}</span>
                        </div>
                    `)
                )
                .addTo(map.current);
            markersRef.current.push(origenMarker);

            // ----- 2. MARCADORES DE PASAJEROS + LÍNEAS -----
            const lineFeatures = [];

            passengers.forEach((p) => {
                const coord = [p.lng, p.lat];
                bounds.extend(coord);

                // Popup con distancia (si existe)
                let distanceText = "";
                if (p.distance !== null) {
                    distanceText = `<br/><span style="font-size: 12px; color: #888;">📏 ${p.distance.toFixed(2)} km del origen</span>`;
                }

                const popupHtml = `
                    <div style="font-family: Arial, sans-serif; min-width: 180px; padding: 4px 0;">
                        <strong style="font-size: 14px;">${escapeHtml(p.name)}</strong><br/>
                        <span style="font-size: 13px; color: #333;">${escapeHtml(p.address)}</span>
                        ${distanceText}
                    </div>
                `;

                const marker = new mapboxgl.Marker({ color: "#2e7d32" })
                    .setLngLat(coord)
                    .setPopup(new mapboxgl.Popup().setHTML(popupHtml))
                    .addTo(map.current);

                markersRef.current.push(marker);

                // Crear feature de línea desde origen a este pasajero
                if (p.distance !== null) {
                    lineFeatures.push({
                        type: "Feature",
                        geometry: {
                            type: "LineString",
                            coordinates: [
                                [origen.lng, origen.lat],
                                [p.lng, p.lat],
                            ],
                        },
                        properties: {
                            distance: p.distance,
                        },
                    });
                }
            });

            // ----- 3. CAPA DE LÍNEAS (con color según distancia) -----
            if (lineFeatures.length > 0) {
                const distances = lineFeatures.map((f) => f.properties.distance);
                const maxDist = Math.max(...distances);

                const sourceId = "lines-source";
                const layerId = "lines-layer";

                if (map.current.getSource(sourceId)) {
                    map.current.removeSource(sourceId);
                }
                if (map.current.getLayer(layerId)) {
                    map.current.removeLayer(layerId);
                }

                map.current.addSource(sourceId, {
                    type: "geojson",
                    data: {
                        type: "FeatureCollection",
                        features: lineFeatures,
                    },
                });

                map.current.addLayer({
                    id: layerId,
                    type: "line",
                    source: sourceId,
                    layout: {
                        "line-cap": "round",
                        "line-join": "round",
                    },
                    paint: {
                        "line-width": 3,
                        "line-opacity": 0.7,
                        "line-color": [
                            "interpolate",
                            ["linear"],
                            ["get", "distance"],
                            0,
                            "#3b82f6", // azul (cerca)
                            maxDist * 0.5,
                            "#f59e0b", // ámbar (medio)
                            maxDist,
                            "#ef4444", // rojo (lejos)
                        ],
                    },
                });

                linesSourceRef.current = sourceId;
            }

            // Ajustar vista
            if (bounds.isEmpty()) {
                map.current.setCenter([origen.lng, origen.lat]);
                map.current.setZoom(12);
            } else {
                map.current.fitBounds(bounds, {
                    padding: 70,
                    maxZoom: 15,
                });
            }
        });

        return () => {
            markersRef.current.forEach((m) => m.remove());
            markersRef.current = [];
            if (map.current) {
                map.current.remove();
                map.current = null;
            }
            if (mapContainer.current) {
                mapContainer.current.innerHTML = "";
            }
        };
    }, [origen, passengers]);

    // Mensajes cuando falta origen o pasajeros
    if (!origen) {
        return (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 320,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 8,
                    background: "#f8fafc",
                    color: "#64748b",
                    fontWeight: 600,
                    textAlign: "center",
                    padding: 20,
                }}
            >
                No se ha podido determinar un origen válido.
                <br />
                <span style={{ fontSize: 14, fontWeight: 400 }}>
                    {origenid
                        ? "El origen indicado no tiene coordenadas válidas."
                        : "Intenta asegurar que los pasajeros tengan 'puntosubida' y que exista una parada con ese nombre."}
                </span>
            </div>
        );
    }

    if (passengers.length === 0) {
        return (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 320,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 8,
                    background: "#f8fafc",
                    color: "#64748b",
                    fontWeight: 600,
                }}
            >
                No hay pasajeros con coordenadas de destino válidas.
            </div>
        );
    }

    return (
        <div
            ref={mapContainer}
            style={{
                width: "100%",
                height: "100%",
                minHeight: 320,
                borderRadius: 8,
                overflow: "hidden",
            }}
        />
    );
};

export default PassengerLocationMap;